import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';

import {
    MEDIA_ATTRIBUTES_TYPES_IDS,
    MEDIA_TAGS_TYPES_IDS,
    MEDIA_TYPES_IDS,
    PRODUCTION_DOCUMENT_TYPES,
} from 'constants/consts';
import {
    AGGREGATION_ON,
    AGGREGATION_TYPES as CLIENT_AGGREGATION_TYPES,
    CONTRIBUTORS,
    PERIOD_VALUE,
    PRODUCTION_VALIDITY_IDENTIFIERS,
    PROFESSION_IDS,
    PROFESSIONS_AVAILABILITY,
    SORT_OPTION_TYPES,
    SORT_OPTION_VALUES,
    SPOTLIGHT_PRODUCTION_TYPE,
    URL_SLUG_TYPES,
    VIDEO_ATTRIBUTE_SLUGS,
    VIEW_MODES_VALUE,
    WORK_ROLE_TYPE_IDS,
} from 'constants/index';

import { deleteOne, getList, getRequest, getWholeList } from 'utils/API';
import { formatResponseToList } from 'utils/crudUtils';
import { createDate } from 'utils/date';
import { getEntityIdWithPrefix, transformEntity } from 'utils/globals';
import { transformFiltersToParams } from 'utils/globals/filters';
import { getQuery, postQuery, transformQueryFn, withSelectFn, withSelectTransformers } from 'utils/globals/queries';
import { getEntityPerformedDates, getMinMaxProductionDates } from 'utils/productions';
import { getLanguage } from 'utils/queriesUtil';
import { useMutation } from 'utils/react-query';

import {
    selectData,
    selectTotalCount,
    transformDynamicFilters,
    transformDynamicFiltersForCastingTool,
} from 'containers/Globals/transformers';

import { AGGREGATION_TYPES, ENTITY_TYPES, FILTER_TYPES } from 'operabase-router/constants';

import {
    selectAggregatedOptions,
    selectProductionsForMediaSlug,
    transformProductionsForMediaSlug,
    transformRestrictedWorksFromRoleOverlap,
} from './transformers';

export const ENTITY_TYPE_KEY_MAP = {
  [ENTITY_TYPES.PROFILE]: 'individual_id',
  [ENTITY_TYPES.PRODUCTION]: 'id',
  [ENTITY_TYPES.WORK]: 'work_id',
  [ENTITY_TYPES.ORGANIZATION]: 'organization_id',
};

const ENDPOINTS = {
  STATIC: {
    PRODUCTIONS: 'productions',
    ORGANIZATIONS: 'organizations',
    AGENCIES: 'agencies',
    MEDIA: 'media',
    PERFORMANCES: 'performances',
    CASTS_ENDPOINT: 'productions/castings',
    PRODUCTIONS_REVIVALS: 'productions/revivals',
    PRODUCTION_AGGREGATIONS: 'productions/aggregations',
    ROLE_OVERLAP: 'works/roles/overlap',
    WORK_ROLES: 'works/roles/',
  },
  DYNAMIC: {
    PRODUCTION_CASTS: productionId => `productions/${productionId}/contributions`,
    ENTITY_ROLE_OVERLAP: profileId => `profiles/${profileId}/creditRichness`,
  },
};

const getProductionListParams = ({ entityType, entityId, filters, limit, page }) => {
  const entityParams = {};

  const ctxEntityId = getEntityIdWithPrefix({ entityType, entityId });

  if ([ENTITY_TYPES.ORGANIZATION, ENTITY_TYPES.PROFILE].includes(entityType)) {
    if (entityType === ENTITY_TYPES.ORGANIZATION) {
      entityParams.approved_by = PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG;
    } else {
      entityParams.include_virtual_contributors = true;
    }

    entityParams.ctx_entity = ctxEntityId;
  } else if (ENTITY_TYPE_KEY_MAP[entityType] && entityId) {
    entityParams[ENTITY_TYPE_KEY_MAP[entityType]] = entityId;
  }

  const params = {
    queryParams: {
      ...entityParams,
      ...filters,
    },
    pagination: {
      limit,
      page,
    },
  };

  return params;
};

const fetchProductionMedia = async ({ productionIds, mediaLimit }, cookies) => {
  if (!productionIds?.length) {
    return null;
  }

  const mediaParams = {
    queryParams: {
      media_type_id: [MEDIA_TYPES_IDS.IMAGE, MEDIA_TYPES_IDS.VIDEO],
      media_tag_id: productionIds,
      media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PRODUCTION,
      exclude_media_attribute_id: MEDIA_ATTRIBUTES_TYPES_IDS.BANNER,
      sort: 'order,-performance',
      distinct: 'production_id',
      distinct_limit: mediaLimit,
    },
    pagination: {
      limit: (productionIds?.length || 0) * mediaLimit,
      page: 1,
    },
  };

  const groupedProductionMedia = await getList(ENDPOINTS.STATIC.MEDIA, mediaParams, cookies).then(response => {
    const data = (response?.data?.data || [])?.map(media => {
      const mediaTypeId = media?.mediaType?.id;
      const videoAttributeType =
        mediaTypeId === MEDIA_TYPES_IDS.VIDEO
          ? media?.attributes?.find(attribute => attribute?.attributeType?.slug === VIDEO_ATTRIBUTE_SLUGS.VIDEO_TYPE)
              ?.name
          : null;

      return {
        id: media?.id,
        blurHash: media?.blurHash,
        copyright: media?.copyright,
        credit: media?.credit,
        source_info: media?.source_info,
        production: {
          id: media?.production?.id,
          name: media?.production?.name,
        },
        thumbnail: media?.thumbnail,
        title: media?.title,
        url: media?.url,
        mediaType: media?.mediaType,
        file: media?.file,
        ...(videoAttributeType && { videoType: videoAttributeType }),
      };
    });

    return groupBy(data, file => file?.production?.id);
  });

  return groupedProductionMedia;
};

const fetchProductions = async ({ entityType, entityId, filters, limit = 10, mediaLimit, page }, cookies) => {
  const params = getProductionListParams({ entityType, entityId, filters, limit, page });

  const productions = await getList(ENDPOINTS.STATIC.PRODUCTIONS, params, cookies);
  let productionMedia;

  if (mediaLimit > 0) {
    productionMedia = await fetchProductionMedia(
      {
        productionIds: productions?.data?.data?.map(production => production?.id),
        mediaLimit: 5,
      },
      cookies,
    );
  }

  return {
    ...productions?.data,
    data: productions?.data?.data?.map(production => ({
      ...transformEntity(production, ENTITY_TYPES.PRODUCTION),
      media: productionMedia?.[production?.id],
    })),
  };
};

const fetchPerformances = async ({ entityType, entityId, filters, limit = 10, mediaLimit, page }, cookies) => {
  const params = getProductionListParams({ entityType, entityId, filters, limit, page });

  const performances = await getList(ENDPOINTS.STATIC.PERFORMANCES, params, cookies);
  let productionMedia;

  if (mediaLimit > 0) {
    productionMedia = await fetchProductionMedia(
      {
        productionIds: performances?.data?.data?.map(performance => performance?.production?.id),
        mediaLimit: 5,
      },
      cookies,
    );
  }

  return {
    ...performances?.data,
    data: performances?.data?.data?.map(performance => ({
      ...transformEntity(performance, ENTITY_TYPES.PRODUCTION),
      media: productionMedia?.[performance?.production?.id],
    })),
  };
};

const fetchProduction = ({ id, program_published }, cookies) =>
  fetchProductions({ filters: { id, program_published } }, cookies).then(response => response?.data?.[0] || null);

const filterPrimaryCrew = (member, profileId) => {
  const isPrimaryCrew = [PROFESSION_IDS.STAGE_DIRECTOR, PROFESSION_IDS.CONDUCTOR].includes(member?.profession?.id);

  if (profileId) {
    return isPrimaryCrew || member?.profile?.id === profileId;
  }

  return isPrimaryCrew;
};

export const transformProductionData = ({
  production,
  profileId,
  onlyPrimaryCrew = false,
  onlyMinMaxDates = false,
  excludeKeys = [],
}) => ({
  ...omit(transformEntity(production, ENTITY_TYPES.PRODUCTION), ['privacy', ...excludeKeys]),
  ...(onlyMinMaxDates && {
    dates: [],
    performanceDatesCount: production?.performances?.length,
    ...getMinMaxProductionDates(production),
  }),
  ...(onlyPrimaryCrew && {
    casts: production?.casts?.filter(member => filterPrimaryCrew(member, profileId)),
    crew: production?.crew?.filter(member => filterPrimaryCrew(member, profileId)),
  }),
  artistPerformanceDatesCount: getEntityPerformedDates(production, profileId)?.artistPerformedDates || 0,
});

// TODO: FB-000000 Remove this function after refactoring Production queris
const fetchProductionsData = ({ entityType, entityId, limit = 5, page = 1, filters = {}, viewType }, cookies) => {
  const params = {
    pagination: {
      limit,
      page,
    },
    queryParams: {
      [ENTITY_TYPE_KEY_MAP[entityType]]: entityId,
      sort: '-year',
      ...filters,
    },
  };

  if (entityType === ENTITY_TYPES.PROFILE) {
    params.queryParams.ctx_entity = getEntityIdWithPrefix({ entityType, entityId });
    delete params.queryParams[ENTITY_TYPE_KEY_MAP[entityType]];
  }

  const API = viewType === VIEW_MODES_VALUE.PERFORMANCE ? ENDPOINTS.STATIC.PERFORMANCES : ENDPOINTS.STATIC.PRODUCTIONS;

  return getList(API, params, cookies).then(response => {
    const formattedResponse = formatResponseToList(response);

    return {
      ...formattedResponse,
      data: formattedResponse?.data?.map(production =>
        transformProductionData({
          production,
          onlyMinMaxDates: false,
          profileId: entityId,
        }),
      ),
    };
  });
};

const fetchProductions_DEPRECATED = (
  { entityType, entityId, limit = 5, page = 1, viewMode, filters = {}, asCount = false },
  cookies,
) => {
  let appliedFilters = {};

  if (entityType !== ENTITY_TYPES.PRODUCTION) {
    let sort = SORT_OPTION_TYPES?.YEAR_ASC.value;

    if (filters?.sort) {
      sort = filters?.sort;
    } else if (filters?.[URL_SLUG_TYPES.PRODUCTION_PERIOD] === PERIOD_VALUE.ALL) {
      sort = SORT_OPTION_TYPES?.YEAR_DESC.value;
    }

    appliedFilters = {
      ...filters,
      sort,
    };
  }

  if (filters?.production_id) {
    appliedFilters = {
      ...appliedFilters,
      ...(filters?.[URL_SLUG_TYPES.PRODUCTION_PERIOD] !== PERIOD_VALUE.ALL && {
        date_from: filters?.date_from,
      }),
    };
  }

  const params = {
    pagination: {
      limit: entityType === ENTITY_TYPES.PRODUCTION ? 1 : limit,
      page,
    },
    queryParams: {
      [ENTITY_TYPE_KEY_MAP[entityType]]: entityId,
      ...appliedFilters,
    },
  };

  const url = [VIEW_MODES_VALUE.DAY, VIEW_MODES_VALUE.CALENDAR, VIEW_MODES_VALUE.PERFORMANCE].includes(viewMode)
    ? ENDPOINTS.STATIC.PERFORMANCES
    : ENDPOINTS.STATIC.PRODUCTIONS;

  return getList(url, params, cookies).then(response => {
    if (entityType === ENTITY_TYPES.PRODUCTION) {
      return transformEntity(response?.data?.data?.[0], ENTITY_TYPES.PRODUCTION);
    }

    if (asCount) {
      return response?.data?.total;
    }

    const formattedResponse = formatResponseToList(response);

    return {
      ...formattedResponse,
      data: formattedResponse?.data?.map(production =>
        transformProductionData({
          production,
          onlyMinMaxDates: false,
        }),
      ),
    };
  });
};

const fetchProductionsWithMedia = async ({ entityType, entityId, page = 1, limit, filters = {} }, cookies) => {
  const params = {
    pagination: {
      limit,
      page,
    },
    queryParams: {
      ...(entityId && { [ENTITY_TYPE_KEY_MAP[entityType]]: entityId }),
      sort: '-year',
      ...filters,
    },
  };

  const productions = await getList(ENDPOINTS.STATIC.PRODUCTIONS, params, cookies).then(response =>
    formatResponseToList(response),
  );
  const productionMedia = await fetchProductionMedia(
    {
      productionIds: productions?.data?.map(production => production?.id),
      mediaLimit: 5,
    },
    cookies,
  );

  return {
    ...productions,
    data: productions?.data?.map(production => ({
      ...production,
      media: productionMedia?.[production?.id],
    })),
  };
};

const fetchProductionDescriptions = (id, cookies) => {
  const params = {
    queryParams: {
      production_id: id,
      document_type: PRODUCTION_DOCUMENT_TYPES.DESCRIPTION,
    },
  };

  return getWholeList(`${ENDPOINTS.STATIC.PRODUCTIONS}/${id}/documents`, params, cookies).then(
    response => response?.data,
  );
};

const fetchProductionBooklets = (id, cookies) => {
  const params = {
    queryParams: {
      production_id: id,
      document_type: PRODUCTION_DOCUMENT_TYPES.BOOKLET,
    },
  };

  return getWholeList(`${ENDPOINTS.STATIC.PRODUCTIONS}/${id}/documents`, params, cookies).then(
    response => response?.data?.data,
  );
};

const fetchProductionSyopsis = (id, cookies) => {
  const params = {
    queryParams: {
      production_id: id,
    },
  };

  return getWholeList(`${ENDPOINTS.STATIC.PRODUCTIONS}/${id}/synopsis`, params, cookies).then(
    response => response?.data?.data,
  );
};

const fetchAboutMedia = ({ id, limit = 5 }, cookies) => {
  const params = {
    queryParams: {
      media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PRODUCTION,
      media_tag_id: id,
      media_type_id: MEDIA_TYPES_IDS.IMAGE,
    },
    pagination: {
      limit,
    },
  };

  return getList(ENDPOINTS.STATIC.MEDIA, params, cookies).then(response => formatResponseToList(response)?.data);
};

const getValidAggregationFilterValues = ({
  aggregationType,
  aggregationFilter,
  query,
  filters,
  entityId,
  entityType,
  limit,
}) => {
  const entityKey = [ENTITY_TYPE_KEY_MAP[entityType]];
  // NOTE: we need to clean below and use it from operabase-router/constants
  const FILTER_KEY_MAP = {
    company: CLIENT_AGGREGATION_TYPES.COMPANY,
    festival: CLIENT_AGGREGATION_TYPES.FESTIVAL,
    venue: AGGREGATION_TYPES[FILTER_TYPES.VENUE],
    work: AGGREGATION_TYPES[FILTER_TYPES.WORK],
    language: AGGREGATION_TYPES[FILTER_TYPES.LANGUAGE],
    surtitle: AGGREGATION_TYPES[FILTER_TYPES.SURTITLE],
    creator: AGGREGATION_TYPES[FILTER_TYPES.CREATOR],
  };

  let appliedFilters = {
    aggregation_type: aggregationType,
    [entityKey]: entityId,
    ...(filters?.date ? filters?.date : {}),
    ...(query ? { aggregation_query: query } : {}),
  };

  Object.keys(filters).forEach(key => {
    const filterKey = FILTER_KEY_MAP[key];

    if (filterKey) {
      let filterValues = filters[key]?.map(({ id }) => id) || [];

      if (aggregationFilter === entityKey) {
        filterValues = [...filterValues, entityId];
      }

      appliedFilters = {
        ...appliedFilters,
        [filterKey]: filterValues,
      };
    }
  });

  return {
    queryParams: appliedFilters,
    pagination: {
      limit,
    },
  };
};

const fetchProductionAggregations = async (
  { entityType, entityId, query, aggregationType, queryFilters: filters, limit },
  cookies,
) => {
  const params = getValidAggregationFilterValues({
    aggregationType,
    aggregationFilter: CLIENT_AGGREGATION_TYPES.COMPANY,
    filters,
    entityType,
    entityId,
    query,
    limit,
  });

  return getList(`${ENDPOINTS.STATIC.PRODUCTIONS}/aggregations/`, params, cookies).then(response => response?.data);
};

const fetchSpotlightProductions = ({ limit, page = 1, filters = {} }) => {
  const params = {
    queryParams: {
      sort: 'year',
      ...filters,
    },
    pagination: {
      limit,
      page,
    },
  };

  return getList(ENDPOINTS.STATIC.PRODUCTIONS, params).then(response => response?.data);
};

export const fetchProductionsList = ({ productionsIds, limit, entityId, entityType }) => {
  const params = {
    queryParams: {
      id: productionsIds,
      ...(entityId && entityType && { [ENTITY_TYPE_KEY_MAP[entityType]]: entityId }),
    },
    pagination: {
      limit,
    },
  };

  return getList(ENDPOINTS.STATIC.PRODUCTIONS, params).then(response => response?.data);
};

const fetchPerformances_DEPRECATED = ({ entityType, entityId, filters, limit, page }, cookies) => {
  const params = {
    pagination: {
      limit,
      page,
    },
    queryParams: {
      ...(entityId && entityType && { [ENTITY_TYPE_KEY_MAP[entityType]]: entityId }),
      ...omit(filters, URL_SLUG_TYPES.PRODUCTION_PERIOD),
    },
  };

  return getList(ENDPOINTS.STATIC.PERFORMANCES, params, cookies).then(response => {
    const formattedResponse = formatResponseToList(response);

    return {
      ...formattedResponse,
      data: formattedResponse?.data?.map(production =>
        transformProductionData({
          production,
          profileId: entityType === ENTITY_TYPES.PROFILE && entityId,
          onlyPrimaryCrew: true,
          onlyMinMaxDates: true,
        }),
      ),
    };
  });
};

const fetchProductionsCasting = ({ filters, limit = 75, page }) => {
  const params = {
    queryParams: {
      ...filters,
    },
    pagination: {
      limit,
      page,
    },
  };

  return getList(ENDPOINTS.STATIC.CASTS_ENDPOINT, params).then(response => response?.data);
};

const fetchAggregations = (
  {
    aggregationOn,
    aggregationType,
    filters = {},
    limit = 300,
    page = 1,
    includeAggTypeInFilter = false,
    sort = SORT_OPTION_TYPES.NAME_ASC.value,
  },
  cookies,
) => {
  const params = {
    queryParams: {
      aggregation_type: aggregationType,
      sort,
      ...omit(filters, includeAggTypeInFilter ? [] : [aggregationType]),
    },
    pagination: {
      limit,
      page,
    },
  };

  let endpoint = `${ENDPOINTS.STATIC.PRODUCTIONS}/aggregations`;

  if (aggregationOn === AGGREGATION_ON.ORGANIZATIONS) {
    endpoint = `${ENDPOINTS.STATIC.ORGANIZATIONS}/aggregations`;
  } else if (aggregationOn === AGGREGATION_ON.MANAGERS) {
    endpoint = `${ENDPOINTS.STATIC.ORGANIZATIONS}/aggregations`;
    params.queryParams.organization_type_id = 70;

    // Transform aggregation types for organizations endpoint
    if (aggregationType === 'profession_id') {
      params.queryParams.aggregation_type = 'representee_profession_id';
    } else if (aggregationType === 'organization_type_id') {
      params.queryParams.aggregation_type = 'representee_organization_type_id';
    }
  }

  return getList(endpoint, params, cookies).then(response => response?.data);
};

const fetchFilterStats = ({ types, filters, limit = 1 }, cookies) => {
  const facetMap = types?.reduce((acc, type) => {
    const slug = AGGREGATION_TYPES[type];

    if (slug) {
      acc[slug] = type;
    }

    return acc;
  }, {});
  const params = {
    queryParams: {
      facets: Object.keys(facetMap),
      ...filters,
    },
    pagination: {
      limit,
    },
    skipTotalCheck: true,
  };

  return getList(`${ENDPOINTS.STATIC.PRODUCTIONS}/aggregations`, params, cookies).then(response => {
    const stats = response?.data?.meta || {};

    return Object.keys(stats)?.reduce((acc, value) => {
      const type = facetMap[value];

      if (type) {
        acc[type] = stats[value];
      }

      return acc;
    }, {});
  });
};

export function useDeleteProductionContributionMutation(options = {}) {
  return useMutation(
    ({ productionId, castId }) => deleteOne(ENDPOINTS.DYNAMIC.PRODUCTION_CASTS(productionId), castId),
    options,
  );
}

const fetchCastingResults = ({ filters, limit = 75, page }) => {
  const params = {
    queryParams: {
      ...filters,
    },
    pagination: {
      limit,
      page,
    },
  };

  return getList(`${ENDPOINTS.STATIC.CASTS_ENDPOINT}v2`, params).then(response => response?.data);
};

const fetchCoverAndDebutAggregations = ({ filters = {} }, cookies) => {
  const commonParams = {
    pagination: {
      limit: 10,
      page: 1,
    },
  };
  const omitAggregationTypes = [
    AGGREGATION_TYPES[FILTER_TYPES.COVER_ROLE],
    AGGREGATION_TYPES[FILTER_TYPES.ROLE_DEBUT],
    AGGREGATION_TYPES[FILTER_TYPES.PRODUCER_DEBUT],
  ];
  const filterParams = omit(filters, omitAggregationTypes);
  const endpoint = `${ENDPOINTS.STATIC.PRODUCTIONS}/aggregations`;

  const promises = [
    getList(
      endpoint,
      {
        queryParams: {
          aggregation_type: AGGREGATION_TYPES[FILTER_TYPES.COVER_ROLE],
          [AGGREGATION_TYPES[FILTER_TYPES.COVER_ROLE]]: true,
          ...filterParams,
        },
        ...commonParams,
      },
      cookies,
    ),
    getList(
      endpoint,
      {
        queryParams: {
          aggregation_type: AGGREGATION_TYPES[FILTER_TYPES.ROLE_DEBUT],
          [AGGREGATION_TYPES[FILTER_TYPES.ROLE_DEBUT]]: true,
          ...filterParams,
        },
        ...commonParams,
      },
      cookies,
    ),
    getList(
      endpoint,
      {
        queryParams: {
          aggregation_type: AGGREGATION_TYPES[FILTER_TYPES.PRODUCER_DEBUT],
          [AGGREGATION_TYPES[FILTER_TYPES.PRODUCER_DEBUT]]: true,
          ...filterParams,
        },
        ...commonParams,
      },
      cookies,
    ),
  ];

  return Promise.allSettled(promises).then(resp => {
    const TYPES = [
      AGGREGATION_TYPES[FILTER_TYPES.COVER_ROLE],
      AGGREGATION_TYPES[FILTER_TYPES.ROLE_DEBUT],
      AGGREGATION_TYPES[FILTER_TYPES.PRODUCER_DEBUT],
    ];
    return {
      data: resp
        ?.reduce((acc, { status, value }) => {
          if (status === 'fulfilled' && value?.data?.total > 0) {
            const itemType = value?.data?.filters?.find(({ type: aggregationType }) => TYPES.includes(aggregationType))
              ?.type;

            acc.push({
              data: value?.data,
              type: itemType,
            });
          }

          return acc;
        }, [])
        .sort((a, b) => {
          const totalA = a?.data?.data?.[0]?.total ?? 0;
          const totalB = b?.data?.data?.[0]?.total ?? 0;
          return totalB - totalA;
        }),
    };
  });
};

const fetchDynamicFiltersForCastingTool = ({ filters }, cookies) =>
  Promise.allSettled([
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
          [AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE]]: [
            CONTRIBUTORS.PRODUCER,
            CONTRIBUTORS.VENUE,
            CONTRIBUTORS.CO_PRODUCER,
          ],
        },
        includeAggTypeInFilter: true,
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE],
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
          [AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID]]: [
            PROFESSION_IDS.STAGE_DIRECTOR,
            PROFESSION_IDS.CONDUCTOR,
            PROFESSION_IDS.CHOREOGRAPHER,
          ],
        },
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
        includeAggTypeInFilter: true,
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
          work_role_type_id: WORK_ROLE_TYPE_IDS.CHARACTER,
        },
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
          [AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE]]: CONTRIBUTORS.CREW,
          availability: PROFESSIONS_AVAILABILITY.CASTING_TOOL,
        },
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
          work_role_type_id: WORK_ROLE_TYPE_IDS.INSTRUMENTALIST,
        },
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
        },
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_ENSEMBLE_TYPE_ID],
      },
      cookies,
    ),
  ]).then(results => {
    // NOTE: Process and filter successful API responses
    const processedResults = {
      contributionTypes: results[0]?.status === 'fulfilled' && { data: results[0].value?.data || [] },
      primaryProfession: results[1]?.status === 'fulfilled' && { data: results[1].value?.data || [] },
      cast: results[2]?.status === 'fulfilled' && {
        data: results[2].value?.data || [],
        total: results[2].value?.total || 0,
      },
      crew: results[3]?.status === 'fulfilled' && {
        data: results[3].value?.data || [],
        total: results[3].value?.total || 0,
      },
      instrumentalist: results[4]?.status === 'fulfilled' && {
        data: results[4].value?.data || [],
        total: results[4].value?.total || 0,
      },
      ensemble: results[5]?.status === 'fulfilled' && {
        data: {
          data: results[5].value?.data || [],
          total: results[5].value?.total || 0,
        },
      },
    };

    // NOTE: Remove failed responses and transform valid data
    return transformDynamicFiltersForCastingTool(
      Object.fromEntries(Object.entries(processedResults).filter(([_, v]) => v)),
    );
  });

const fetchDynamicFilters = ({ filters }, cookies) =>
  Promise.allSettled([
    fetchAggregations(
      {
        filters: {
          ...filters,
          [AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE]]: [
            CONTRIBUTORS.PRODUCER,
            CONTRIBUTORS.VENUE,
            CONTRIBUTORS.CO_PRODUCER,
          ],
        },
        includeAggTypeInFilter: true,
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE],
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters,
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_ENSEMBLE_TYPE_ID],
      },
      cookies,
    ),
    fetchAggregations(
      {
        filters: {
          ...filters,
          include_contrib_count: true,
          [AGGREGATION_TYPES[FILTER_TYPES.PROFESSION]]: filters[AGGREGATION_TYPES[FILTER_TYPES.PROFESSION]],
        },
        aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
        includeAggTypeInFilter: true,
      },
      cookies,
    ),
  ]).then(results => {
    const getData = result =>
      result.status === 'fulfilled' && result?.value?.data?.length > 0 ? result.value.data : [];

    return transformDynamicFilters([getData(results[0]), getData(results[1]), getData(results[2])]);
  });

const fetchSeasons = ({ filters }, cookies) =>
  fetchAggregations({ filters, aggregationType: CLIENT_AGGREGATION_TYPES.SEASON }, cookies);

export const fetchPageStats = ({ filters: filterParams, language: lang }, cookies) => {
  const query = {
    queryParams: {
      facets: 'has_tickets,has_online',
      ...filterParams,
      lang,
    },
  };

  return getRequest(ENDPOINTS.STATIC.PRODUCTION_AGGREGATIONS, query, cookies).then(response => response?.data);
};

export const fetchProductionCounts = ({ filters: filterParams, language: lang }, cookies) => {
  const query = {
    queryParams: {
      ...filterParams,
      lang,
      limit: 1,
    },
  };

  return getRequest(ENDPOINTS.STATIC.PRODUCTIONS, query, cookies).then(response => response?.data);
};

const fetchRolesOverlap = ({ filters, limit = 2, page }) => {
  const endPoint = filters?.profileId
    ? ENDPOINTS.DYNAMIC.ENTITY_ROLE_OVERLAP(filters.profileId)
    : ENDPOINTS.STATIC.ROLE_OVERLAP;
  const params = {
    queryParams: {
      ...filters,
    },
    pagination: {
      limit,
      page,
    },
  };

  return getList(endPoint, params).then(response => response?.data);
};

const fetchWorkRoles = ({ filters, limit = 1000, page }) => {
  const baseParams = {
    pagination: {
      limit,
      page,
    },
  };

  return Promise.allSettled([
    getList(`${ENDPOINTS.STATIC.WORK_ROLES}`, {
      ...baseParams,
      queryParams: {
        ...filters,
        work_role_type_id: 1,
        is_standard: true,
      },
    }),
    getList(`${ENDPOINTS.STATIC.WORK_ROLES}`, {
      ...baseParams,
      queryParams: {
        ...filters,
        work_role_type_id: 1,
        is_standard: false,
      },
    }),
    getList(`${ENDPOINTS.STATIC.WORK_ROLES}`, {
      ...baseParams,
      queryParams: {
        ...filters,
        work_role_type_id: 3,
        is_standard: true,
      },
    }),
    getList(`${ENDPOINTS.STATIC.WORK_ROLES}`, {
      ...baseParams,
      queryParams: {
        ...filters,
        work_role_type_id: 3,
        is_standard: false,
      },
    }),
  ]).then(([castResult, othersResult, instrumentalistsResult, otherInstrumentalistsResult]) => {
    const cast = castResult.status === 'fulfilled' ? castResult.value?.data?.data : [];
    const others = othersResult.status === 'fulfilled' ? othersResult.value?.data?.data : [];
    const instrumentalists =
      instrumentalistsResult.status === 'fulfilled' ? instrumentalistsResult.value?.data?.data : [];
    const otherInstrumentalists =
      otherInstrumentalistsResult.status === 'fulfilled' ? otherInstrumentalistsResult.value?.data?.data : [];

    return {
      cast,
      others,
      instrumentalists,
      other_instrumentalist: otherInstrumentalists,
    };
  });
};

const fetchQuickFilterOptions = (
  {
    aggregationOn = AGGREGATION_ON.PRODUCTIONS,
    aggregationType,
    filters,
    limit = 10,
    sort = SORT_OPTION_VALUES.PROD_COUNT_DESC,
  },
  cookies,
) => {
  if (!aggregationType) {
    return null;
  }

  const transformedFilters =
    aggregationType === AGGREGATION_TYPES[FILTER_TYPES.WORK_TYPE]
      ? {
          ...filters,
          is_work_type_standard: true,
        }
      : filters;

  return fetchAggregations(
    {
      aggregationOn,
      aggregationType,
      filters: transformedFilters,
      limit,
      sort,
    },
    cookies,
  );
};

const defaultConfig = {
  retry: false,
  retryOnMount: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 10 * 60 * 1000,
  getNextPageParam: lastPage => {
    const { limit, page, total } = lastPage;
    const totalFetched = limit * page;

    if (totalFetched < total) {
      return page + 1;
    }

    return undefined;
  },
  getPreviousPageParam: () => false,
};

const queries = {
  getPageProductionStats: getQuery('GET_PAGE_PRODUCTION_STATS', fetchPageStats),
  getProductionCount: getQuery('GET_PRODUCTION_COUNT', transformQueryFn(fetchProductions, selectTotalCount)),
  getProductions: getQuery('GET_PRODUCTIONS', fetchProductions),
  getProductionsForMediaPage: getQuery('GET_PRODUCTIONS_FOR_MEDIA_PAGE', fetchProductions),
  getPerformances: getQuery('GET_PERFORMANCES', fetchPerformances),
  getCountries: getQuery('GET_COUNTRY_AGGREGATIONS', props =>
    fetchAggregations({ ...props, aggregationType: AGGREGATION_TYPES[FILTER_TYPES.COUNTRY] }),
  ),
  getCities: getQuery('GET_CITY_AGGREGATIONS', props =>
    fetchAggregations({ ...props, aggregationType: AGGREGATION_TYPES[FILTER_TYPES.CITY] }),
  ),
  getSeasons: getQuery('GET_SEASONS', fetchSeasons, withSelectFn(selectAggregatedOptions)),
  getFilterCounts: getQuery('GET_FILTER_COUNTS', fetchFilterStats),
  getAggregatedEntities: getQuery('GET_AGGREGATED_ENTITIES', fetchAggregations),
  getFilterOptions: getQuery('GET_FILTER_OPTIONS', fetchAggregations, props => ({
    ...props,
    filters: {
      ...props?.filters,
      ...(props?.aggregationType === AGGREGATION_TYPES.CONTRIBUTOR_ID && {
        only_approved_contributors: true,
      }),
    },
  })),
  getProductionDetails: getQuery('GET_PRODUCTION_DETAILS', fetchProduction),
  getEntityProductionsData: getQuery('GET_ENTITY_PRODUCTIONS_DATA', fetchProductionsData, props => ({
    ...props,
    filters: transformFiltersToParams(props?.filters),
  })),
  getEntityProductions: (
    { entityType, entityId, limit = 5, viewMode = VIEW_MODES_VALUE.SUMMARY, filters = {}, queryConfig = {}, asCount },
    cookies,
    language,
  ) => {
    const transformedFilters = transformFiltersToParams(filters);
    return {
      queryKey: [
        getLanguage(language),
        'GET_ENTITY_PRODUCTIONS',
        `${entityType}`,
        `${entityId}`,
        `${limit}`,
        `${viewMode}`,
        JSON.stringify(transformedFilters),
      ],
      queryFn: async ({ pageParam = 1 }) =>
        fetchProductions_DEPRECATED(
          { entityType, entityId, limit, page: pageParam, viewMode, filters: transformedFilters, asCount },
          cookies,
        ),
      ...defaultConfig,
      ...queryConfig,
    };
  },
  getPerformances_DEPRECATED: ({ entityType, entityId, limit, filters, queryConfig }, cookies, language) => {
    const transformedFilters = transformFiltersToParams(filters);

    return {
      queryKey: [
        getLanguage(language),
        'GET_PERFORMANCES',
        `${entityType}`,
        `${entityId}`,
        `${limit}`,
        JSON.stringify(transformedFilters),
      ],
      queryFn: async ({ pageParam = 1 }) =>
        fetchPerformances_DEPRECATED(
          { entityType, entityId, limit, page: pageParam, filters: transformedFilters },
          cookies,
        ),
      ...defaultConfig,
      ...queryConfig,
    };
  },
  getRentals: getQuery('GET_ENTITY_RENTALS', fetchProductionsWithMedia, props => ({
    limit: 18,
    ...props,
    filters: {
      ...(props.entityId &&
        [ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(props?.entityType) && {
          producer_id: props.entityType == ENTITY_TYPES.ORGANIZATION ? `o${props.entityId}` : `a${props.entityId}`,
        }),
      ...props?.filters,
      has_active_rental: true,
      approved_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
    },
  })),
  getFeaturedProductions: ({ countryId = '', limit = 8, filters, queryConfig = {} }, cookies, language) => {
    const transformedFilters = {
      ...transformFiltersToParams(filters),
      date_from: createDate().format('YYYY-MM-DD'),
      sort: 'year',
      added_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
      approved_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
      ...(countryId && { country_id: countryId }),
      is_featured: true,
    };

    return {
      queryKey: [
        getLanguage(language),
        'GET_FEATURE_PRODUCTIONS',
        `${countryId}`,
        `${limit}`,
        JSON.stringify(transformedFilters),
      ],
      queryFn: async ({ pageParam = 1 }) =>
        fetchProductionsWithMedia({ limit, page: pageParam, filters: transformedFilters }, cookies),
      ...defaultConfig,
      ...queryConfig,
    };
  },
  getProductionDescriptions: ({ id, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_PRODUCTION_DESCRIPTIONS', `${id}`],
    queryFn: async () => fetchProductionDescriptions(id, cookies),
    select: response => response?.data,
    ...defaultConfig,
    ...queryConfig,
  }),
  getAboutImages: ({ id, limit = 5, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_PRODUCTION_ABOUT_IMAGES', `${id}`, `${limit}`],
    queryFn: async () => fetchAboutMedia({ id, limit }, cookies),
    ...defaultConfig,
    ...queryConfig,
  }),
  getProductionBooklet: ({ id, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_PRODUCTION_BOOKLETS', `${id}`],
    queryFn: async () => fetchProductionBooklets(id, cookies),
    ...defaultConfig,
    ...queryConfig,
  }),
  getProductionSynopsis: ({ id, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_PRODUCTION_SYNOPSIS', `${id}`],
    queryFn: async () => fetchProductionSyopsis(id, cookies),
    ...defaultConfig,
    ...queryConfig,
  }),
  getProductionAggregations: (
    { entityType, entityId, query = '', aggregationType, queryFilters, queryConfig = {}, limit = 10 },
    cookies,
    language,
  ) => ({
    queryKey: [
      getLanguage(language),
      'GET_PRODUCTION_AGGREGATIONS',
      `${entityType}`,
      `${entityId}`,
      `${aggregationType}`,
      `${query}`,
      queryFilters,
      limit,
    ],
    queryFn: async () =>
      fetchProductionAggregations({ entityType, entityId, query, aggregationType, queryFilters, limit }, cookies),
    ...defaultConfig,
    ...queryConfig,
  }),
  getSpotlightProductions: (
    { countryId = '', type, filters, limit = 10, queryConfig = {} } = {},
    cookies,
    language,
  ) => {
    const transformedFilters = {
      ...transformFiltersToParams(filters),
      date_from: createDate().format('YYYY-MM-DD'),
      sort: 'year',
      added_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
      approved_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
      ...(countryId && { country_id: countryId }),
      ...(type === SPOTLIGHT_PRODUCTION_TYPE.NEW_RARE && { is_new_and_rare: true }),
      ...(type === SPOTLIGHT_PRODUCTION_TYPE.HIGHLIGHTS && { is_highlight: true }),
    };

    return {
      queryKey: [getLanguage(language), 'GET_SPOTLIGHT_PRODUCTIONS', `${limit}`, JSON.stringify(transformedFilters)],
      queryFn: async ({ pageParam = 1 }) =>
        fetchSpotlightProductions({ page: pageParam, filters: transformedFilters, limit }, cookies),
      ...defaultConfig,
      ...queryConfig,
    };
  },
  getAggregationList: ({ endpoint, queryParams, pagination, queryConfig = {} }, cookies, language) => {
    const query = {
      queryParams,
      pagination: { limit: 20 },
    };

    return {
      queryKey: [getLanguage(language), 'GET_AGGREGATIONS', queryParams],
      queryFn: async () => getList(endpoint, query, pagination, cookies),
      ...defaultConfig,
      ...queryConfig,
    };
  },
  getProductionCasting: getQuery('GET_CASTING_RESULTS', fetchProductionsCasting),
  createProductionRevival: postQuery(ENDPOINTS.STATIC.PRODUCTIONS_REVIVALS),
  getDynamicFilters: getQuery('GET_CONTRIBUTION_EXPRESSION_OPTIONS', fetchDynamicFilters),
  getDynamicFiltersForCastingTool: getQuery('GET_DYNAMIC_FILTERS_FOR_CASTING_TOOL', fetchDynamicFiltersForCastingTool),
  getRentalStats: getQuery('GET_RENTAL_STATS', props =>
    fetchAggregations({ ...props, aggregationType: AGGREGATION_TYPES.RENTAL }),
  ),
  getCastingResults: getQuery('GET_CASTING_RESULTS', fetchCastingResults),
  getWorkRoleOverlap: getQuery(
    'GET_ROLES_OVERLAP',
    transformQueryFn(fetchRolesOverlap, transformRestrictedWorksFromRoleOverlap),
  ),
  getCoverAndDebutOptions: getQuery('GET_COVER_AND_DEBUT_FILTER_OPTIONS', fetchCoverAndDebutAggregations),
  getWorkRoles: getQuery('GET_CASTING_RESULTS', fetchWorkRoles),
  getQuickFilterOptions: getQuery(
    'GET_QUICK_FILTER_OPTIONS',
    fetchQuickFilterOptions,
    withSelectTransformers(selectData),
  ),
  getHomepageFeaturedProductions: getQuery(
    'GET_HOMEPAGE_FEATURED_PRODUCTIONS',
    transformQueryFn(fetchProductionsWithMedia, transformProductionsForMediaSlug),
    withSelectTransformers(selectProductionsForMediaSlug),
  ),
};

export default queries;
